<script setup>
import { computed } from 'vue';
import { useI18n } from 'vue-i18n';
import PriorityIcon from 'dashboard/routes/dashboard/inbox/components/PriorityIcon.vue';
import { formatDistanceToNow } from 'date-fns';
import { ptBR } from 'date-fns/locale';
import MarkdownIt from 'markdown-it';

const props = defineProps({
  item: {
    type: Object,
    required: true,
  },
  conversationData: {
    type: Object,
    default: null,
  },
  loadingConversation: {
    type: Boolean,
    default: false,
  },
  currentStageName: {
    type: String,
    default: '',
  },
  itemDescription: {
    type: String,
    default: '',
  },
});

const emit = defineEmits(['navigate-to-conversation', 'context-menu', 'stage-click']);

const { t } = useI18n();

// Instância do MarkdownIt com configurações padrão
const md = new MarkdownIt({
  html: false,      // Desabilitar HTML inline para segurança
  breaks: true,     // Converter quebras de linha em <br>
  linkify: true     // Converter URLs em links automaticamente
});

// Função para renderizar markdown
const renderMarkdown = (text) => {
  if (!text) return '';
  return md.render(text);
};



const truncatedSenderName = computed(() => {
  const name = conversationInfo.value?.contact?.name;
  const email = conversationInfo.value?.contact?.email;
  const defaultText = t('KANBAN.ITEM_CONVERSATION.NO_CONTACT');
  const text = name || email || defaultText;

  return text.length > 10 ? `${text.substring(0, 10)}...` : text;
});

const formatStageDate = item => {
  if (!item || !item.stage_entered_at) return '';
  try {
    return t('KANBAN.STAGE.TIME_IN_STAGE', {
      time: formatDistanceToNow(new Date(item.stage_entered_at), {
        locale: ptBR,
        addSuffix: false,
      }),
    });
  } catch (error) {
    // console.error('Erro ao calcular tempo na etapa:', error);
    return '';
  }
};

const onNavigateToConversation = (e, conversationDisplayId) => {
  emit('navigate-to-conversation', e, conversationDisplayId);
};

const onContextMenu = (e, conversationId) => {
  emit('context-menu', e, conversationId);
};

const onStageClick = (stage) => {
  emit('stage-click', stage);
};

// Novo computed para extrair o progresso nas etapas
const stageProgress = computed(() => {
  // Busca as etapas no local esperado ou alternativo
  const stages =
    props.item.item_details?.funnel?.stages ||
    props.item.funnel?.stages ||
    null;
  const activities = props.item.item_details?.activities || [];

  if (!stages || !activities.length) {
    return { stages: [], currentStageIndex: -1, completedStages: [], stageTransitions: [] };
  }

  // Converte stages object para array ordenado por position
  const stagesArray = Object.entries(stages)
    .map(([key, stage]) => ({
      key,
      name: stage.name,
      color: stage.color,
      position: stage.position,
    }))
    .sort((a, b) => a.position - b.position);

  // Extrai as mudanças de etapa das atividades
  const stageChanges = activities
    .filter(activity => activity.type === 'stage_changed')
    .sort((a, b) => new Date(a.created_at) - new Date(b.created_at));

  // Encontra todas as etapas pelas quais o item passou
  const visitedStages = new Set();
  stageChanges.forEach(change => {
    if (change.details.new_stage) {
      visitedStages.add(change.details.new_stage);
    }
  });

  // Etapa atual
  const currentStage = props.item.funnel_stage;
  const currentStageIndex = stagesArray.findIndex(
    stage => stage.key === currentStage
  );

  // Determina etapas completadas baseado na ordem das posições
  const completedStages = [];
  if (currentStageIndex >= 0) {
    for (let i = 0; i < currentStageIndex; i++) {
      if (visitedStages.has(stagesArray[i].key)) {
        completedStages.push(stagesArray[i].key);
      }
    }
  }

  // Cria transições apenas entre etapas consecutivas na timeline visual
  const stageTransitions = [];

  // Para cada par de etapas consecutivas na timeline (0-1, 1-2, 2-3, etc.)
  for (let i = 0; i < stagesArray.length - 1; i++) {
    const fromStage = stagesArray[i];
    const toStage = stagesArray[i + 1];
    const transitionKey = `${i}-${i + 1}`;

    // Encontra a transição mais recente entre essas duas etapas consecutivas
    let latestTransition = null;
    let latestDate = null;

    for (const change of stageChanges) {
      if (!change.details?.old_stage || !change.details?.new_stage) continue;

      // Verifica se essa mudança representa uma transição entre as etapas consecutivas atuais
      if (change.details.old_stage === fromStage.key && change.details.new_stage === toStage.key) {
        const changeDate = new Date(change.details.created_at || change.created_at);

        if (!latestDate || changeDate > latestDate) {
          latestDate = changeDate;
          latestTransition = {
            fromStage: fromStage.key,
            toStage: toStage.key,
            fromIndex: i,
            toIndex: i + 1,
            user: change.user || change.details?.user,
            created_at: change.created_at,
            key: transitionKey
          };
        }
      }
    }

    // Só adiciona se encontrou pelo menos uma transição
    if (latestTransition) {
      stageTransitions.push(latestTransition);
    }
  }

  return {
    stages: stagesArray,
    currentStageIndex,
    completedStages,
    currentStage,
    visitedStages: Array.from(visitedStages),
    stageTransitions,
  };
});

const agentInfo = computed(() => {
  // Primeiro tenta pegar assigned_agents
  const agent = props.item.assigned_agents?.[0] || props.item.agent;
  if (!agent) return null;

  return {
    name: agent.name,
    avatar_url: agent.avatar_url || agent.thumbnail || '',
  };
});

// Computed para determinar o status visual (ganho/perdido)
const itemStatus = computed(() => {
  const status = props.item.item_details?.status;
  const currentStage = props.item.funnel_stage;


  // Se tem status explícito, usa ele
  if (status === 'won') {
    return {
      type: 'won',
      label: t('KANBAN.STATUS.WON') || 'Ganho',
      class:
        'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200',
    };
  }
  if (status === 'lost') {
    return {
      type: 'lost',
      label: t('KANBAN.STATUS.LOST') || 'Perdido',
      class: 'bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-200',
    };
  }

  // Caso contrário, tenta inferir pelo estágio
  if (currentStage === 'resolved' || currentStage === 'closed') {
    return {
      type: 'won',
      label: t('KANBAN.STATUS.COMPLETED') || 'Concluído',
      class:
        'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200',
    };
  }

  return {
    type: 'active',
    label: t('KANBAN.STATUS.IN_PROGRESS') || 'Em Andamento',
    class: 'bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-200',
  };
});


// Computed para extrair dados da conversa diretamente do item
const conversationFromItem = computed(() => {
  // Primeiro tenta usar os dados da conversa do item (preferência)
  const conversationData = props.item?.conversation;
  if (conversationData) {
    return {
      id: conversationData.id,
      display_id: conversationData.display_id,
      status: conversationData.status,
      last_activity_at: conversationData.last_activity_at,
      unread_count: conversationData.unread_count,
      label_list: conversationData.label_list || [],
      contact: conversationData.contact,
      inbox: conversationData.inbox,
    };
  }

  // Fallback para a prop conversationData (compatibilidade)
  if (props.conversationData) {
    return props.conversationData;
  }

  return null;
});

// Computed para dados combinados da conversa (prioriza dados do item)
const conversationInfo = computed(() => {
  return conversationFromItem.value;
});

// Computed para descrição truncada com markdown
const truncatedDescription = computed(() => {
  const description = props.item.item_details?.description;
  if (!description) return '';

  // Renderizar markdown primeiro
  const rendered = renderMarkdown(description);
  if (rendered.length <= 150) return rendered;

  // Truncar o HTML mantendo as tags válidas
  const truncated = rendered.substring(0, 150);
  const lastTagStart = truncated.lastIndexOf('<');
  const lastTagEnd = truncated.lastIndexOf('>');

  // Se há uma tag aberta no final, fechar ela
  if (lastTagStart > lastTagEnd) {
    return truncated.substring(0, lastTagStart) + '...';
  }

  return truncated + '...';
});

// Função para formatar data
const formatDate = dateString => {
  try {
    const date = new Date(dateString);
    return date.toLocaleString('pt-BR', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  } catch (error) {
    return dateString;
  }
};
</script>

<template>
  <div class="header-section">

    <!-- Descrição do Item -->
    <div
      v-if="item.item_details?.description"
      class="text-[11px] text-slate-600 dark:text-slate-400 mb-4"
    >
      <div v-html="truncatedDescription" class="prose prose-sm max-w-none dark:prose-invert"></div>
    </div>

    <!-- Indicador de Progresso nas Etapas -->
    <div
      v-if="stageProgress.stages.length > 0"
      class="bg-white dark:bg-slate-800 rounded-lg mb-4"
    >
      <div class="flex items-center justify-between mb-2">
        <span class="text-xs font-medium text-slate-700 dark:text-slate-300 opacity-60">
          {{ t('KANBAN.STAGE.PROGRESS') }}
        </span>
        <span
          class="text-xs text-slate-500 bg-slate-100 dark:bg-slate-700 px-2 py-1 rounded-full"
        >
          {{ stageProgress.currentStageIndex + 1 }} /
          {{ stageProgress.stages.length }}
        </span>
      </div>

      <div class="relative pt-4">
        <!-- Etapas -->
        <div class="flex justify-between relative">
          <!-- Linha de progresso base -->
          <div
            class="absolute top-2.5 left-2.5 h-0.5 bg-slate-200 dark:bg-slate-700 rounded-full"
            style="width: calc(100% - 40px)"
          ></div>

          <!-- Linha de progresso ativa -->
          <div
            class="absolute top-2.5 left-2.5 h-0.5 bg-gradient-to-r from-woot-500 to-woot-600 rounded-full transition-all duration-700 ease-out"
            :style="{
              width:
                stageProgress.currentStageIndex > 0
                  ? `calc((100% - 40px) * ${
                      stageProgress.currentStageIndex /
                      Math.max(stageProgress.stages.length - 1, 1)
                    })`
                  : '0%',
            }"
          ></div>

          <!-- Nomes dos agentes responsáveis pelas transições (apenas desktop) -->
          <div
            v-for="transition in stageProgress.stageTransitions"
            :key="transition.key"
            class="absolute -top-2 flex flex-col items-center justify-center hidden md:flex"
            :style="{
              left: `calc(2.5rem + ${(transition.fromIndex + (transition.toIndex - transition.fromIndex) / 2) / Math.max(stageProgress.stages.length - 1, 1) * 100}%)`,
              transform: 'translateX(-50%)'
            }"
          >
            <div class="bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-600 rounded-lg px-2 py-1 shadow-sm">
              <div class="text-center">
                <span class="text-[9px] font-medium text-slate-700 dark:text-slate-300 whitespace-nowrap block">
                  {{ transition.user?.name || 'Sistema' }}
                </span>
                <span class="text-[8px] text-slate-500 dark:text-slate-400 whitespace-nowrap block">
                  {{ formatDate(transition.created_at) }}
                </span>
              </div>
            </div>
          </div>

          <div
            v-for="(stage, index) in stageProgress.stages"
            :key="stage.key"
            class="flex flex-col items-center group cursor-pointer"
            @click="onStageClick(stage)"
          >
            <!-- Círculo da etapa -->
            <div
              class="relative flex items-center justify-center w-5 h-5 rounded-full transition-all duration-300 group-hover:scale-110 z-10"
              :class="{
                // Etapa atual - azul vibrante
                'bg-woot-500 text-white shadow-lg shadow-woot-500/25':
                  index === stageProgress.currentStageIndex,
                // Etapas completadas - verde sutil
                'bg-green-500 text-white shadow-sm':
                  index < stageProgress.currentStageIndex &&
                  stageProgress.completedStages.includes(stage.key),
                // Etapas visitadas mas não completadas - amarelo suave
                'bg-yellow-400 text-white shadow-sm':
                  stageProgress.visitedStages.includes(stage.key) &&
                  index < stageProgress.currentStageIndex &&
                  !stageProgress.completedStages.includes(stage.key),
                // Etapas anteriores não visitadas - cinza igual às futuras
                'bg-slate-200 dark:bg-slate-700 text-slate-500 dark:text-slate-400':
                  (index < stageProgress.currentStageIndex &&
                    !stageProgress.visitedStages.includes(stage.key)) ||
                  index > stageProgress.currentStageIndex,
              }"
            >
              <!-- Símbolo baseado no status -->
              <span
                v-if="
                  index < stageProgress.currentStageIndex &&
                  stageProgress.completedStages.includes(stage.key)
                "
                class="text-[10px] font-bold"
              >
                ✓
              </span>
              <span v-else class="text-[10px] font-medium">
                {{ index + 1 }}
              </span>
            </div>

            <!-- Nome da etapa -->
            <div class="mt-1 text-center max-w-16">
              <div
                class="text-[10px] font-medium transition-colors duration-200"
                :class="{
                  'text-woot-600 dark:text-woot-400':
                    index === stageProgress.currentStageIndex,
                  'text-green-600 dark:text-green-400':
                    index < stageProgress.currentStageIndex &&
                    stageProgress.completedStages.includes(stage.key),
                  'text-yellow-600 dark:text-yellow-400':
                    stageProgress.visitedStages.includes(stage.key) &&
                    index < stageProgress.currentStageIndex &&
                    !stageProgress.completedStages.includes(stage.key),
                  'text-slate-500 dark:text-slate-400':
                    (index < stageProgress.currentStageIndex &&
                      !stageProgress.visitedStages.includes(stage.key)) ||
                    index > stageProgress.currentStageIndex,
                }"
              >
                {{ stage.name }}
              </div>

              <!-- Badge do estágio atual -->
              <div
                v-if="index === stageProgress.currentStageIndex"
                class="mt-1"
              >
                <span class="inline-flex items-center px-2 py-0.5 rounded-full text-[9px] font-medium bg-woot-100 dark:bg-woot-900 text-woot-700 dark:text-woot-300">
                  Atual
                </span>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- Conversa Relacionada - Largura Total -->
    <div v-if="conversationInfo" class="conversation-section">
      <div class="flex items-center justify-between gap-1 mb-2">
        <div class="flex items-center gap-1">
          <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" class="text-slate-600 dark:text-slate-400 flex-shrink-0">
            <path d="M12 3H4a2 2 0 0 0-2 2v16.286a.71.71 0 0 0 1.212.502l2.202-2.202A2 2 0 0 1 6.828 19H20a2 2 0 0 0 2-2v-4"/>
            <path d="M16 3h6v6"/>
            <path d="m16 9 6-6"/>
          </svg>
          <span class="text-xs text-slate-700 dark:text-slate-300">
            {{ t('KANBAN.FORM.CONVERSATION.LABEL') }}
          </span>
        </div>
          <span class="text-xs text-slate-600 dark:text-slate-400">
            Clique para navegar
          </span>
      </div>
      <div
        class="bg-white dark:bg-slate-800 rounded-lg border border-slate-200/30 dark:border-slate-700/30 p-3 cursor-pointer hover:bg-slate-50 dark:hover:bg-slate-700/50 transition-colors"
        @click="onNavigateToConversation($event, conversationInfo.display_id)"
        @contextmenu="onContextMenu($event, conversationInfo.id)"
      >
        <!-- Contato -->
        <div v-if="conversationInfo.contact" class="flex items-center gap-3 mb-2">
          <img
            v-if="conversationInfo.contact.thumbnail"
            :src="conversationInfo.contact.thumbnail"
            :alt="conversationInfo.contact.name"
            class="w-8 h-8 rounded-full object-cover"
          />
          <div v-else class="w-8 h-8 bg-slate-200 dark:bg-slate-600 rounded-full flex items-center justify-center">
            <span class="text-xs font-medium text-slate-600 dark:text-slate-400">
              {{ conversationInfo.contact.name?.charAt(0)?.toUpperCase() || '?' }}
            </span>
          </div>
          <div class="flex-1">
            <div class="flex items-center justify-between">
              <div class="font-semibold text-sm text-slate-900 dark:text-slate-100">
                <span class="text-xs text-slate-500 dark:text-slate-400 mr-1">#{{ conversationInfo.display_id }}</span>
                {{ conversationInfo.contact.name || t('KANBAN.CONTACT_UNKNOWN') }}
              </div>
            </div>
          </div>
          <div class="flex items-center gap-1">
            <!-- Status -->
            <span
              class="text-xs px-2 py-0.5 rounded-full font-medium"
              :class="{
                'bg-green-100 text-green-700 dark:bg-green-900 dark:text-green-300':
                  conversationInfo.status === 'open',
                'bg-yellow-100 text-yellow-700 dark:bg-yellow-900 dark:text-yellow-300':
                  conversationInfo.status === 'pending',
                'bg-slate-100 text-slate-700 dark:bg-slate-700 dark:text-slate-300':
                  conversationInfo.status === 'resolved',
              }"
            >
              {{ conversationInfo.status }}
            </span>
            <!-- Unread count -->
            <span
              v-if="conversationInfo.unread_count > 0"
              class="flex items-center justify-center h-5 min-w-[1rem] px-1.5 text-xs font-medium bg-red-500 text-white rounded-full shadow-sm"
            >
              {{ conversationInfo.unread_count > 9 ? '9+' : conversationInfo.unread_count }}
            </span>
          </div>
        </div>

        <!-- Informações adicionais -->
        <div class="flex flex-wrap gap-3 text-xs text-slate-600 dark:text-slate-400 mb-2">
          <!-- Última atividade -->
          <div v-if="conversationInfo.last_activity_at" class="flex items-center gap-1">
            <span class="font-medium">{{ t('KANBAN.LAST_ACTIVITY') }}:</span>
            <span>{{ formatDate(conversationInfo.last_activity_at) }}</span>
          </div>
        </div>

        <!-- Labels -->
        <div v-if="conversationInfo.label_list && conversationInfo.label_list.length > 0" class="flex flex-wrap gap-1">
          <span
            v-for="label in conversationInfo.label_list"
            :key="label"
            class="text-xs px-2 py-0.5 bg-blue-100 dark:bg-blue-900 text-blue-700 dark:text-blue-300 rounded-full"
          >
            {{ label }}
          </span>
        </div>

        <!-- Dados da inbox - Canto inferior esquerdo -->
        <div class="flex justify-between items-end mt-2">
          <!-- Inbox info - Lado esquerdo -->
          <div v-if="conversationInfo.inbox" class="flex items-center gap-1 text-xs text-slate-600 dark:text-slate-400 bg-slate-50 dark:bg-slate-700/50 px-2 py-1 rounded-md">
            <span class="font-medium">{{ conversationInfo.inbox.name }}</span>
            <span class="px-1.5 py-0.5 bg-slate-100 dark:bg-slate-700 rounded text-xs text-slate-700 dark:text-slate-300">
              {{ conversationInfo.inbox.channel_type?.replace('Channel::', '') || 'Web' }}
            </span>
          </div>

          <!-- Phone number do contato - Lado direito -->
          <div v-if="conversationInfo.contact?.phone_number" class="flex items-center gap-1 text-xs text-slate-600 dark:text-slate-400 bg-slate-50 dark:bg-slate-700/50 px-2 py-1 rounded-md">
            <svg xmlns="http://www.w3.org/2000/svg" width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" class="text-slate-500 dark:text-slate-400">
              <path d="M22 16.92v3a2 2 0 0 1-2.18 2 19.79 19.79 0 0 1-8.63-3.07 19.5 19.5 0 0 1-6-6 19.79 19.79 0 0 1-3.07-8.67A2 2 0 0 1 4.11 2h3a2 2 0 0 1 2 1.72 12.84 12.84 0 0 0 .7 2.81 2 2 0 0 1-.45 2.11L8.09 9.91a16 16 0 0 0 6 6l1.27-1.27a2 2 0 0 1 2.11-.45 12.84 12.84 0 0 0 2.81.7A2 2 0 0 1 22 16.92z"/>
            </svg>
            {{ conversationInfo.contact.phone_number }}
          </div>
        </div>
      </div>
    </div>

    <!-- Grid de informações principais -->
    <div class="info-grid">


    </div>
  </div>
</template>

<style scoped>
.header-section {
  @apply bg-white dark:bg-slate-800
    rounded-lg
    p-4 mb-4
    border border-slate-100 dark:border-slate-700;
}

.info-grid {
  @apply flex justify-start mt-3;
}

.conversation-section {
  @apply mb-4;
}

.info-card {
  @apply relative overflow-hidden flex flex-col gap-1 p-3 rounded-lg transition-all duration-200
    bg-white dark:bg-slate-800 border dark:border-slate-700;
}

.info-card:nth-child(1) {
  @apply bg-white dark:bg-slate-800 border-slate-200 dark:border-slate-700;
}

.info-card:nth-child(2) {
  @apply bg-white dark:bg-slate-800 border-slate-200 dark:border-slate-700;
}

.info-card:nth-child(3) {
  @apply bg-white dark:bg-slate-800 border-slate-200 dark:border-slate-700;
}

.info-card:nth-child(4) {
  @apply bg-white dark:bg-slate-800 border-slate-200 dark:border-slate-700;
}

.info-label {
  @apply text-xs font-semibold uppercase tracking-wide text-slate-600 dark:text-slate-400;
}

.info-content {
  @apply text-sm font-medium text-slate-900 dark:text-slate-100;
}

.info-card:hover {
  @apply transform -translate-y-0.5 shadow-sm dark:shadow-slate-900/50;
}

.loading-text,
.no-data-text {
  @apply text-sm text-slate-500;
}

.value-text {
  @apply font-semibold text-n-teal-9 dark:text-n-teal-4;
}

.stage-progress-container {
  @apply bg-slate-50 dark:bg-slate-800/50 rounded-lg p-4 border border-slate-200 dark:border-slate-700;
}

.stage-progress-container .relative {
  @apply py-2;
}

/* Estilos para markdown renderizado */
.prose {
  font-size: 12px !important;
  @apply leading-relaxed;
}

.prose p {
  @apply mb-1 last:mb-0;
  font-size: 12px !important;
}

.prose ul {
  @apply list-disc list-inside mb-1 space-y-1;
}

.prose ol {
  @apply list-decimal list-inside mb-1 space-y-1;
}

.prose li {
  @apply leading-relaxed;
  font-size: 12px !important;
}

.prose strong {
  @apply font-semibold;
  font-size: 12px !important;
}

.prose em {
  @apply italic;
  font-size: 12px !important;
}

.prose code {
  @apply bg-slate-100 dark:bg-slate-800 px-1 py-0.5 rounded font-mono;
  font-size: 12px !important;
}

.prose pre {
  @apply bg-slate-100 dark:bg-slate-800 p-2 rounded font-mono overflow-x-auto mb-1;
  font-size: 12px !important;
}

:deep(a) {
  @apply text-woot-500 hover:text-woot-600 underline;
}

/* Responsividade para progresso em telas menores */
@media (max-width: 640px) {
  .stage-progress-container .flex.justify-between {
    @apply gap-1;
  }

  .stage-progress-container .text-xs {
    @apply text-[10px];
  }

  .stage-progress-container .w-8.h-8 {
    @apply w-6 h-6;
  }
}
</style> 